import React, { useState } from 'react';
// @mui
import {
  Stack,
  Divider,
  Checkbox,
  TableRow,
  MenuItem,
  TableCell,
  IconButton,
  Typography,
  Link,
} from '@mui/material';
// hooks
// utils
import { fDate } from '../../../../utils/formatTime';
import { fData2 } from '../../../../utils/formatNumber';
// @types
import { IFile } from '../../../../@types/file';
// components
import Iconify from '../../../../components/iconify';
import MenuPopover from '../../../../components/menu-popover';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../../../components/confirm-dialog';
import FileThumbnail from '../../../../components/file-thumbnail';
//
import FileDetailsDrawer from '../portal/FileDetailsDrawer';
import { AsyncButton } from '@subflow-frontend/components/AsyncButton';
import { Draggable } from '@hello-pangea/dnd';
import { useCopyFileLinkToClipboard } from '@subflow-frontend/hooks/content/useCopyFileLinkToClipboard';
import { useCanAccess } from '@subflow-frontend/guards/RoleBasedGuard';
import { InviteMemberRequestDtoRoleEnum } from '@subflow/api-client';
import { accessTypesById } from '@subflow-frontend/sections/content/library/types';
import ConfirmationModal from '@subflow-frontend/components/ConfirmationModal';
import { useDebugContentOrderFeatureFlag } from '@subflow-frontend/hooks/featureFlag/features/useDebugContentOrderFeatureFlag';

// ----------------------------------------------------------------------

type Props<ID extends string | number = number> = {
  row: IFile<ID>;
  selected: boolean;
  index: number;
  isDragAndDropEnabled: boolean;
  onSelectRow: VoidFunction;
  onDeleteRow?: () => Promise<void>;
  onClickRow?: VoidFunction;
  onActionClick?: (action: string) => void;
  disableDetailsPanel?: boolean;
  disableFolderSelection?: boolean;
  debugContentOrderEnabled?: boolean;
};

const FileTableRow = <ID extends string | number = number>({
  row,
  selected,
  isDragAndDropEnabled,
  onSelectRow,
  onDeleteRow,
  onClickRow,
  onActionClick,
  index,
  disableDetailsPanel,
  disableFolderSelection,
  debugContentOrderEnabled,
}: Props<ID>) => {
  const {
    name,
    size,
    type,
    dateModified,
    dateCreated,
    isFavorited,
    published,
    accessType,
  } = row;
  const { enqueueSnackbar } = useSnackbar();

  const { copyFileLinkToClipboard } = useCopyFileLinkToClipboard();
  //
  // const [inviteEmail, setInviteEmail] = useState('');
  //
  // const [openShare, setOpenShare] = useState(false);

  const [openDetails, setOpenDetails] = useState(false);

  const [openConfirm, setOpenConfirm] = useState(false);

  const [favorited, setFavorited] = useState(isFavorited);

  const deleteAccessGranted = useCanAccess([
    InviteMemberRequestDtoRoleEnum.Owner,
    InviteMemberRequestDtoRoleEnum.Admin,
    InviteMemberRequestDtoRoleEnum.Manager,
  ]);

  const allOptionsAccessGranted = useCanAccess([
    InviteMemberRequestDtoRoleEnum.Owner,
    InviteMemberRequestDtoRoleEnum.Admin,
    InviteMemberRequestDtoRoleEnum.Manager,
    InviteMemberRequestDtoRoleEnum.Member,
  ]);

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleFavorite = () => {
    setFavorited(!favorited);
  };

  const handleOpenDetails = () => {
    handleClosePopover();
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  // const handleOpenShare = () => {
  //   setOpenShare(true);
  // };

  // const handleCloseShare = () => {
  //   setOpenShare(false);
  // };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleCopy = async (skipValidation?: boolean) => {
    handleClosePopover();
    if (onActionClick) {
      onActionClick('copyLink');
    }
  };

  const handleSend = () => {
    handleClosePopover();
    if (onActionClick) {
      onActionClick('send');
    }
  };

  const handleEdit = () => {
    handleClosePopover();
    if (onActionClick) {
      onActionClick('edit');
    }
  };

  const handleMoveTo = () => {
    handleClosePopover();
    if (onActionClick) {
      onActionClick('moveTo');
    }
  };

  const isFile = row.fileType === 'file';
  const isFolder = row.fileType === 'folder';

  const handleRowClick = () => {
    if (isFolder) {
      onClickRow();
    } else {
      handleOpenDetails();
    }
  };

  return (
    <>
      <Draggable
        isDragDisabled={!isDragAndDropEnabled}
        key={`steps[${index}]`}
        draggableId={`${index}`}
        index={index}
      >
        {(provided, snapshot) => (
          <TableRow
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            sx={{
              borderRadius: 1,
              '& .MuiTableCell-root': {
                bgcolor: 'background.default',
              },
              ...(!!snapshot.combineTargetFor &&
                isFolder && {
                  '& .MuiTableCell-root': {
                    bgcolor: 'action.selected',
                  },
                }),
              ...(openDetails && {
                '& .MuiTableCell-root': {
                  color: 'text.primary',
                  typography: 'subtitle2',
                  bgcolor: 'background.default',
                },
              }),
            }}
          >
            <TableCell
              padding="checkbox"
              sx={{
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              }}
            >
              {!(disableFolderSelection && row.fileType === 'folder') && (
                <Checkbox
                  checked={selected}
                  onDoubleClick={() => console.log('ON DOUBLE CLICK')}
                  onClick={onSelectRow}
                />
              )}
            </TableCell>
            {debugContentOrderEnabled && (
              <TableCell
                align="left"
                onClick={handleRowClick}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {row.id}
              </TableCell>
            )}
            <TableCell onClick={handleRowClick}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <FileThumbnail
                  file={row}
                  showPreview={row.fileType === 'file'}
                  sx={{ borderRadius: 1 }}
                />

                <Typography
                  noWrap
                  variant="inherit"
                  sx={{ maxWidth: 360, cursor: 'pointer' }}
                >
                  {name}
                </Typography>
              </Stack>
            </TableCell>
            {debugContentOrderEnabled && (
              <TableCell
                align="left"
                onClick={handleRowClick}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {row.position}
              </TableCell>
            )}
            <TableCell
              align="left"
              onClick={handleRowClick}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {row.fileType === 'file' && fData2(size)}
            </TableCell>

            <TableCell
              align="center"
              onClick={handleRowClick}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {type}
            </TableCell>
            <TableCell
              align="left"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              onClick={handleRowClick}
            >
              {accessTypesById[accessType].title}
            </TableCell>
            <TableCell
              align="left"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              onClick={handleRowClick}
            >
              {published ? 'Published' : 'Unpublished'}{' '}
            </TableCell>
            <TableCell
              align="left"
              onClick={handleRowClick}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {fDate(dateCreated)}
            </TableCell>

            <TableCell
              align="right"
              sx={{
                whiteSpace: 'nowrap',
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
              }}
            >
              {/*<Checkbox*/}
              {/*  color="warning"*/}
              {/*  icon={<Iconify icon="eva:star-outline" />}*/}
              {/*  checkedIcon={<Iconify icon="eva:star-fill" />}*/}
              {/*  checked={favorited}*/}
              {/*  onChange={handleFavorite}*/}
              {/*  sx={{ p: 0.75 }}*/}
              {/*/>*/}

              {allOptionsAccessGranted && onActionClick && (
                <IconButton
                  color={openPopover ? 'inherit' : 'default'}
                  onClick={handleOpenPopover}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        )}
      </Draggable>

      {onActionClick && (
        <MenuPopover
          open={openPopover}
          onClose={handleClosePopover}
          arrow="right-top"
          sx={{ width: 160 }}
        >
          <MenuItem onClick={handleSend}>
            <Iconify icon="ic:baseline-send" />
            Send
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClosePopover();
              handleCopy();
            }}
          >
            <Iconify icon="eva:link-2-fill" />
            Copy Link
          </MenuItem>
          <MenuItem onClick={handleOpenDetails}>
            <Iconify icon="material-symbols:preview" />
            View Details
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <Iconify icon="ic:baseline-edit" />
            Edit Details
          </MenuItem>
          {isFile && (
            <Link href={row.url} download color="inherit" target={'_blank'}>
              <MenuItem>
                <Iconify icon="ic:baseline-download" />
                Download
              </MenuItem>
            </Link>
          )}
          <MenuItem onClick={handleMoveTo}>
            <Iconify icon="material-symbols:drive-file-move-outline" />
            Move To
          </MenuItem>
          {deleteAccessGranted && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />

              <MenuItem
                onClick={() => {
                  handleOpenConfirm();
                  handleClosePopover();
                }}
                sx={{ color: 'error.main' }}
              >
                <Iconify icon="eva:trash-2-outline" />
                Delete
              </MenuItem>
            </>
          )}
        </MenuPopover>
      )}

      {!disableDetailsPanel && (
        <FileDetailsDrawer
          item={row}
          favorited={favorited}
          onFavorite={handleFavorite}
          onCopyLink={handleCopy}
          open={openDetails}
          onClose={handleCloseDetails}
          onDelete={onDeleteRow}
          onEdit={() => onActionClick('edit')}
        />
      )}

      {/*<FileShareDialog*/}
      {/*  open={openShare}*/}
      {/*  shared={shared}*/}
      {/*  inviteEmail={inviteEmail}*/}
      {/*  onChangeInvite={handleChangeInvite}*/}
      {/*  onCopyLink={handleCopy}*/}
      {/*  onClose={() => {*/}
      {/*    handleCloseShare();*/}
      {/*    setInviteEmail('');*/}
      {/*  }}*/}
      {/*/>*/}

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <AsyncButton
            variant="contained"
            color="error"
            onClickAsync={onDeleteRow}
            onAsyncActionComplete={handleCloseConfirm}
          >
            Delete
          </AsyncButton>
        }
      />
    </>
  );
};
export default FileTableRow;
